import dayjs from 'dayjs';
import {
  FinancialTransactionFormData,
  FinancialTransactionInputData,
} from '@/features/dashboard/financialTransactions/schemas/financialTransactionSchema.ts';
import {
  CreateFinancialTransactionRequestDto,
  UpdateFinancialTransactionRequestDto,
} from '@/features/dashboard/financialTransactions/types/FinancialTransactionRequestDto.ts';
import { FinancialTransactionDto } from '@/features/dashboard/financialTransactions/types/FinancialTransactionDto.ts';
import {
  TransactionAllocationDto,
  TransactionAllocationType,
} from '@/features/dashboard/financialTransactions/types/TransactionAllocation.ts';
import { TransactionDirection } from '@/features/dashboard/financialTransactions/types/TransactionDirection.ts';
import { getAmountDirection } from '@/features/dashboard/financialTransactions/types/TransactionDirection.utils.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import timeUtils from '@/utils/timeUtils.ts';

type AllocationFormData = FinancialTransactionFormData['allocations'][number];

/**
 * Operator wpisuje kwoty dodatnie, znak nadaje kierunek. Reguła backendu jest
 * twarda: znak alokacji musi zgadzać się ze znakiem transakcji (HANDOFF §7.8).
 */
const signed = (value: number, direction: TransactionDirection): number =>
  direction === TransactionDirection.OUTGOING ? -value : value;

const toAllocationDto = (
  allocation: AllocationFormData,
  currency: Currency,
  direction: TransactionDirection,
): TransactionAllocationDto => {
  const amount = { amount: signed(allocation.amount, direction), currency };

  return direction === TransactionDirection.OUTGOING
    ? {
        type: TransactionAllocationType.EXPENSE,
        expensePlannedTransactionId: allocation.expensePlannedTransactionId,
        amount,
      }
    : {
        type: TransactionAllocationType.BOOKING,
        bookingId: allocation.bookingId,
        amount,
      };
};

const deriveDirection = (
  dto: FinancialTransactionDto,
): TransactionDirection => {
  const firstAllocation = dto.allocations[0];
  if (firstAllocation) {
    return firstAllocation.type === TransactionAllocationType.EXPENSE
      ? TransactionDirection.OUTGOING
      : TransactionDirection.INCOMING;
  }
  return getAmountDirection(dto.amount.amount);
};

export const financialTransactionRequestDtoMapper = {
  fromFormToCreateRequest: (
    form: FinancialTransactionFormData,
  ): CreateFinancialTransactionRequestDto => ({
    clientId:
      form.direction === TransactionDirection.INCOMING
        ? form.clientId || undefined
        : undefined,
    amount: {
      amount: signed(form.amount, form.direction),
      currency: form.currency,
    },
    transactionDate: timeUtils.toJavaLocalDateTime(
      form.transactionDate,
      form.transactionTime,
    ),
    paymentMethod: form.paymentMethod,
    description: form.description || undefined,
    allocations: form.allocations.map((allocation) =>
      toAllocationDto(allocation, form.currency, form.direction),
    ),
  }),

  fromFormToUpdateRequest: (
    form: FinancialTransactionFormData,
  ): UpdateFinancialTransactionRequestDto => ({
    amount: {
      amount: signed(form.amount, form.direction),
      currency: form.currency,
    },
    transactionDate: timeUtils.toJavaLocalDateTime(
      form.transactionDate,
      form.transactionTime,
    ),
    description: form.description || undefined,
    allocations: form.allocations.map((allocation) =>
      toAllocationDto(allocation, form.currency, form.direction),
    ),
  }),

  fromDtoToForm: (
    dto: FinancialTransactionDto,
  ): FinancialTransactionInputData => {
    const transactionDateTime = dayjs(dto.transactionDate);
    const direction = deriveDirection(dto);

    return {
      direction,
      clientId: dto.clientId ?? '',
      amount: Math.abs(dto.amount.amount),
      currency: dto.amount.currency,
      paymentMethod: dto.paymentMethod,
      transactionDate: transactionDateTime.toDate(),
      transactionTime: transactionDateTime.format('HH:mm'),
      description: dto.description ?? '',
      allocations: dto.allocations.map((allocation) => ({
        type: allocation.type,
        bookingId:
          allocation.type === TransactionAllocationType.BOOKING
            ? allocation.bookingId
            : '',
        expensePlannedTransactionId:
          allocation.type === TransactionAllocationType.EXPENSE
            ? allocation.expensePlannedTransactionId
            : '',
        amount: Math.abs(allocation.amount.amount),
      })),
    };
  },
};
